import { brand } from '@/config/brand';
import type { Locale } from '@/domain/ids';
import { isolateAuto } from '@/lib/bidi';
import { ar, type Messages } from './messages/ar';
import { en } from './messages/en';
import { fr } from './messages/fr';
import type { MessageKey, MessageParams, MessageValue, PluralForms } from './types';

export type { Messages };
export type TKey = MessageKey<Messages>;
export type Translate = (key: TKey, params?: MessageParams) => string;

export const CATALOGUES: Record<Locale, Messages> = { ar, fr: fr as Messages, en: en as Messages };

export const DIRECTION: Record<Locale, 'rtl' | 'ltr'> = { ar: 'rtl', fr: 'ltr', en: 'ltr' };

/** BCP 47 tags. Tunisian schools use Western digits, so Arabic forces the latn numbering system. */
export const LOCALE_TAG: Record<Locale, string> = {
  ar: 'ar-TN-u-nu-latn',
  fr: 'fr-TN',
  en: 'en-GB',
};

function lookup(catalogue: Messages, key: string): MessageValue | undefined {
  let node: unknown = catalogue;
  for (const part of key.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  if (typeof node === 'string') return node;
  if (typeof node === 'object' && node !== null && 'other' in node) return node as PluralForms;
  return undefined;
}

const pluralRules = new Map<Locale, Intl.PluralRules>();
function pluralCategory(locale: Locale, count: number): Intl.LDMLPluralRule {
  let rules = pluralRules.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(LOCALE_TAG[locale]);
    pluralRules.set(locale, rules);
  }
  return rules.select(count);
}

export function pickPlural(forms: PluralForms, locale: Locale, count: number): string {
  if (count === 0 && forms.zero) return forms.zero;
  return forms[pluralCategory(locale, count)] ?? forms.other;
}

export function formatNumber(
  locale: Locale,
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(LOCALE_TAG[locale], options).format(value);
}

function interpolate(template: string, locale: Locale, params: MessageParams = {}): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    if (value === undefined) return match;
    return typeof value === 'number' ? formatNumber(locale, value) : value;
  });
}

/** Creates a translator. Missing keys fall back to Arabic, then to the key itself. */
export function createTranslator(locale: Locale): Translate {
  const catalogue = CATALOGUES[locale];
  return (key, params) => {
    const value = lookup(catalogue, key) ?? lookup(ar, key);
    if (value === undefined) {
      if (import.meta.env.DEV) console.warn(`[i18n] missing message "${key}"`);
      return key;
    }
    const template =
      typeof value === 'string' ? value : pickPlural(value, locale, Number(params?.count ?? 0));
    // {app} is always the configured brand name, isolated for mixed-direction text.
    return interpolate(template, locale, { app: isolateAuto(brand.name[locale]), ...params });
  };
}
